import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/authStore';
import type {
  ApiResponse,
  LoginRequest,
  AdminLoginResponse,
  CallLog,
  CallLogsResponse,
  CallLogFilters,
  CallLogStatistics,
  Recording,
  UploadRecordingRequest,
  Device,
  DevicesResponse,
  DeviceFilters,
  Branch,
  CreateBranchRequest,
  User,
  CreateUserRequest,
  UpdateUserRequest,
  Admin,
  CreateAdminRequest,
  UpdateAdminRequest,
  LoginActivitiesResponse,
  LoginActivityFilters,
  LoginActivityStatistics,
  ProductivityTrackingData,
  Pagination,
  DeviceEvent,
  DeviceEventsResponse,
  DeviceEventFilters,
  DeviceEventStatistics,
  NetworkStatistics,
  EventType,
  DeviceActivitiesResponse,
  DeviceActivityFilters,
  RecordingLifecycleEvent,
  RecordingLifecycleEventsResponse,
  RecordingLifecycleFilters,
  RecordingLifecycleStatistics,
  Attendance,
  AttendanceResponse,
  AttendanceFilters,
  AttendanceStatistics,
  ProductivityReport,
} from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem('auth_token');
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError<ApiResponse<any>>) => {
        const isLoginRequest = error.config?.url?.includes('/admin/login');
        if (!isLoginRequest && (error.response?.status === 401 || error.response?.status === 403)) {
          useAuthStore.getState().logout();
        }
        return Promise.reject(error);
      }
    );
  }

  // Admin Auth endpoints
  async login(data: LoginRequest): Promise<ApiResponse<AdminLoginResponse>> {
    const response = await this.api.post<ApiResponse<AdminLoginResponse>>('/admin/login', data);
    return response.data;
  }

  async logout(): Promise<ApiResponse<null>> {
    const response = await this.api.post<ApiResponse<null>>('/admin/logout');
    return response.data;
  }

  async getMe(): Promise<ApiResponse<{ admin: Admin }>> {
    const response = await this.api.get<ApiResponse<{ admin: Admin }>>('/admin/me');
    return response.data;
  }

  // Call Logs endpoints
  async getCallLogs(filters?: CallLogFilters): Promise<ApiResponse<CallLogsResponse>> {
    const response = await this.api.get<ApiResponse<CallLogsResponse>>('/admin/call-logs', {
      params: filters,
    });
    return response.data;
  }

  async getCallLog(id: number): Promise<ApiResponse<{ call_log: CallLog }>> {
    const response = await this.api.get<ApiResponse<{ call_log: CallLog }>>(`/admin/call-logs/${id}`);
    return response.data;
  }

  async getCallLogStatistics(period?: 'daily' | 'weekly' | 'monthly'): Promise<ApiResponse<CallLogStatistics>> {
    const response = await this.api.get<ApiResponse<CallLogStatistics>>('/admin/call-logs/statistics', {
      params: { period: period || 'daily' },
    });
    return response.data;
  }

  async generateReport(filters?: {
    user_id?: number;
    branch_id?: number;
    min_duration?: number;
    call_type?: string;
    date?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.get<ApiResponse<any>>('/admin/call-logs/report', {
      params: filters,
    });
    return response.data;
  }

  async generateDateWiseReport(filters?: {
    user_id?: number;
    min_duration?: number;
    call_type?: string;
    start_date?: string;
    end_date?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.get<ApiResponse<any>>('/admin/call-logs/report/datewise', {
      params: filters,
    });
    return response.data;
  }

  async deleteCallLog(id: number): Promise<ApiResponse<null>> {
    const response = await this.api.delete<ApiResponse<null>>(`/admin/call-logs/${id}`);
    return response.data;
  }

  async getProductivityTracking(id: number): Promise<ApiResponse<ProductivityTrackingData>> {
    const response = await this.api.get<ApiResponse<ProductivityTrackingData>>(`/admin/call-logs/${id}/productivity-tracking`);
    return response.data;
  }

  async exportCallLogsExcel(filters?: CallLogFilters): Promise<void> {
    const response = await this.api.get('/admin/call-logs/export/excel', {
      params: filters,
      responseType: 'blob',
    });

    // PERFORMANCE FIX: Wrap in try-finally to prevent memory leak if error occurs
    const url = window.URL.createObjectURL(new Blob([response.data]));
    try {
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `call-logs-${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      // Always revoke the URL to prevent memory leak
      window.URL.revokeObjectURL(url);
    }
  }

  async exportCallLogsPdf(filters?: CallLogFilters): Promise<void> {
    const response = await this.api.get('/admin/call-logs/export/pdf', {
      params: filters,
      responseType: 'blob',
    });

    // PERFORMANCE FIX: Wrap in try-finally to prevent memory leak if error occurs
    const url = window.URL.createObjectURL(new Blob([response.data]));
    try {
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `call-logs-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      // Always revoke the URL to prevent memory leak
      window.URL.revokeObjectURL(url);
    }
  }

  // Recordings endpoints
  async getRecordings(callLogId: number): Promise<ApiResponse<{ recordings: Recording[] }>> {
    const response = await this.api.get<ApiResponse<{ recordings: Recording[] }>>(
      `/admin/call-logs/${callLogId}/recordings`
    );
    return response.data;
  }

  async deleteRecording(id: number): Promise<ApiResponse<null>> {
    const response = await this.api.delete<ApiResponse<null>>(`/admin/call-recordings/${id}`);
    return response.data;
  }

  // Devices endpoints
  async getDevices(filters?: DeviceFilters): Promise<ApiResponse<DevicesResponse>> {
    const response = await this.api.get<ApiResponse<DevicesResponse>>('/admin/devices', {
      params: filters,
    });
    return response.data;
  }

  async getDevice(id: number): Promise<ApiResponse<{ device: Device }>> {
    const response = await this.api.get<ApiResponse<{ device: Device }>>(`/admin/devices/${id}`);
    return response.data;
  }

  async deleteDevice(id: number): Promise<ApiResponse<null>> {
    const response = await this.api.delete<ApiResponse<null>>(`/admin/devices/${id}`);
    return response.data;
  }

  async logoutDevice(id: number): Promise<ApiResponse<null>> {
    const response = await this.api.post<ApiResponse<null>>(`/admin/devices/${id}/logout`);
    return response.data;
  }

  // Branch endpoints
  async getBranches(filters?: { status?: string; search?: string; page?: number; per_page?: number; all?: boolean }): Promise<ApiResponse<Branch[]>> {
    const response = await this.api.get<ApiResponse<Branch[]>>('/admin/branches', {
      params: filters,
    });
    return response.data;
  }

  async getBranch(id: number): Promise<ApiResponse<Branch>> {
    const response = await this.api.get<ApiResponse<Branch>>(`/admin/branches/${id}`);
    return response.data;
  }

  async createBranch(data: CreateBranchRequest): Promise<ApiResponse<Branch>> {
    const response = await this.api.post<ApiResponse<Branch>>('/admin/branches', data);
    return response.data;
  }

  async updateBranch(id: number, data: CreateBranchRequest): Promise<ApiResponse<Branch>> {
    const response = await this.api.put<ApiResponse<Branch>>(`/admin/branches/${id}`, data);
    return response.data;
  }

  async deleteBranch(id: number): Promise<ApiResponse<null>> {
    const response = await this.api.delete<ApiResponse<null>>(`/admin/branches/${id}`);
    return response.data;
  }

  // User management endpoints (Android app users)
  async getUsers(filters?: {
    branch_id?: number;
    status?: string;
    search?: string;
    user_id?: number;
    name?: string;
    page?: number;
    per_page?: number;
    all?: boolean;
  }): Promise<ApiResponse<User[]> & { pagination?: Pagination }> {
    const response = await this.api.get<ApiResponse<User[]> & { pagination?: Pagination }>('/admin/users', {
      params: filters,
    });
    return response.data;
  }

  async getUser(id: number): Promise<ApiResponse<User>> {
    const response = await this.api.get<ApiResponse<User>>(`/admin/users/${id}`);
    return response.data;
  }

  async createUser(data: CreateUserRequest): Promise<ApiResponse<User>> {
    const response = await this.api.post<ApiResponse<User>>('/admin/users', data);
    return response.data;
  }

  async updateUser(id: number, data: UpdateUserRequest): Promise<ApiResponse<User>> {
    const response = await this.api.put<ApiResponse<User>>(`/admin/users/${id}`, data);
    return response.data;
  }

  async deleteUser(id: number): Promise<ApiResponse<null>> {
    const response = await this.api.delete<ApiResponse<null>>(`/admin/users/${id}`);
    return response.data;
  }

  async forceLogoutUser(id: number): Promise<ApiResponse<null>> {
    const response = await this.api.post<ApiResponse<null>>(`/admin/users/${id}/force-logout`);
    return response.data;
  }

  // Admin management endpoints
  async getAdmins(filters?: { admin_role?: string; branch_id?: number; status?: string; search?: string }): Promise<ApiResponse<Admin[]>> {
    const response = await this.api.get<ApiResponse<Admin[]>>('/admin/admins', {
      params: filters,
    });
    return response.data;
  }

  async getAdmin(id: number): Promise<ApiResponse<Admin>> {
    const response = await this.api.get<ApiResponse<Admin>>(`/admin/admins/${id}`);
    return response.data;
  }

  async createAdmin(data: CreateAdminRequest): Promise<ApiResponse<Admin>> {
    const response = await this.api.post<ApiResponse<Admin>>('/admin/admins', data);
    return response.data;
  }

  async updateAdmin(id: number, data: UpdateAdminRequest): Promise<ApiResponse<Admin>> {
    const response = await this.api.put<ApiResponse<Admin>>(`/admin/admins/${id}`, data);
    return response.data;
  }

  async deleteAdmin(id: number): Promise<ApiResponse<null>> {
    const response = await this.api.delete<ApiResponse<null>>(`/admin/admins/${id}`);
    return response.data;
  }

  // Login Activity endpoints
  async getLoginActivities(filters?: LoginActivityFilters): Promise<ApiResponse<LoginActivitiesResponse>> {
    const response = await this.api.get<ApiResponse<LoginActivitiesResponse>>('/admin/login-activities', {
      params: filters,
    });
    return response.data;
  }

  async getLoginActivityStatistics(filters?: { start_date?: string; end_date?: string }): Promise<ApiResponse<LoginActivityStatistics>> {
    const response = await this.api.get<ApiResponse<LoginActivityStatistics>>('/admin/login-activities/statistics', {
      params: filters,
    });
    return response.data;
  }

  async getUserLoginActivities(userId: number, filters?: { per_page?: number; page?: number }): Promise<ApiResponse<LoginActivitiesResponse>> {
    const response = await this.api.get<ApiResponse<LoginActivitiesResponse>>(`/admin/login-activities/user/${userId}`, {
      params: filters,
    });
    return response.data;
  }

  async getAdminLoginActivities(adminId: number, filters?: { per_page?: number; page?: number }): Promise<ApiResponse<LoginActivitiesResponse>> {
    const response = await this.api.get<ApiResponse<LoginActivitiesResponse>>(`/admin/login-activities/admin/${adminId}`, {
      params: filters,
    });
    return response.data;
  }

  // Device Events
  async getDeviceEvents(filters?: DeviceEventFilters): Promise<ApiResponse<DeviceEventsResponse>> {
    const response = await this.api.get<ApiResponse<DeviceEventsResponse>>('/admin/device-events', {
      params: filters,
    });
    return response.data;
  }

  async getDeviceEventStatistics(filters?: Omit<DeviceEventFilters, 'page' | 'per_page'>): Promise<ApiResponse<DeviceEventStatistics>> {
    const response = await this.api.get<ApiResponse<DeviceEventStatistics>>('/admin/device-events/statistics', {
      params: filters,
    });
    return response.data;
  }

  async getCriticalEvents(hours?: number): Promise<ApiResponse<{ critical_events: DeviceEvent[]; count: number; hours: number }>> {
    const response = await this.api.get<ApiResponse<{ critical_events: DeviceEvent[]; count: number; hours: number }>>('/admin/device-events/critical', {
      params: { hours },
    });
    return response.data;
  }

  async getNetworkStats(hours?: number): Promise<ApiResponse<NetworkStatistics>> {
    const response = await this.api.get<ApiResponse<NetworkStatistics>>('/admin/device-events/network-stats', {
      params: { hours },
    });
    return response.data;
  }

  async getDeviceEventTimeline(deviceId: number, filters?: { start_date?: string; end_date?: string; event_types?: EventType[] }): Promise<ApiResponse<{ device: Device; timeline: DeviceEvent[] }>> {
    const response = await this.api.get<ApiResponse<{ device: Device; timeline: DeviceEvent[] }>>(`/admin/device-events/device/${deviceId}/timeline`, {
      params: filters,
    });
    return response.data;
  }

  // Device Activities endpoints
  async getDeviceActivities(filters?: DeviceActivityFilters): Promise<ApiResponse<DeviceActivitiesResponse>> {
    const response = await this.api.get<ApiResponse<DeviceActivitiesResponse>>('/admin/device-activities', {
      params: filters,
    });
    return response.data;
  }

  async getUserDeviceActivities(userId: number, filters?: { per_page?: number; page?: number }): Promise<ApiResponse<DeviceActivitiesResponse>> {
    const response = await this.api.get<ApiResponse<DeviceActivitiesResponse>>(`/admin/device-activities/user/${userId}`, {
      params: filters,
    });
    return response.data;
  }

  // Get device events for a specific user
  async getUserDeviceEvents(userId: number, filters?: Omit<DeviceEventFilters, 'user_id'>): Promise<ApiResponse<DeviceEventsResponse>> {
    const response = await this.api.get<ApiResponse<DeviceEventsResponse>>('/admin/device-events', {
      params: {
        ...filters,
        user_id: userId,
      },
    });
    return response.data;
  }

  // Recording Lifecycle - Summary (one row per call)
  async getRecordingLifecycleSummary(filters?: RecordingLifecycleFilters): Promise<ApiResponse<RecordingLifecycleEventsResponse>> {
    const response = await this.api.get<ApiResponse<RecordingLifecycleEventsResponse>>('/admin/recording-lifecycle-summary', {
      params: filters,
    });
    return response.data;
  }

  // Recording Lifecycle Events (detailed timeline - multiple rows per call)
  async getRecordingLifecycleEvents(filters?: RecordingLifecycleFilters): Promise<ApiResponse<RecordingLifecycleEventsResponse>> {
    const response = await this.api.get<ApiResponse<RecordingLifecycleEventsResponse>>('/admin/recording-lifecycle-events', {
      params: filters,
    });
    return response.data;
  }

  // Summary statistics (based on summary table - faster)
  async getRecordingLifecycleSummaryStatistics(filters?: Omit<RecordingLifecycleFilters, 'page' | 'per_page'>): Promise<ApiResponse<RecordingLifecycleStatistics>> {
    const response = await this.api.get<ApiResponse<RecordingLifecycleStatistics>>('/admin/recording-lifecycle-summary/statistics', {
      params: filters,
    });
    return response.data;
  }

  // Detailed event statistics (based on events table)
  async getRecordingLifecycleStatistics(filters?: Omit<RecordingLifecycleFilters, 'page' | 'per_page'>): Promise<ApiResponse<RecordingLifecycleStatistics>> {
    const response = await this.api.get<ApiResponse<RecordingLifecycleStatistics>>('/admin/recording-lifecycle-events/statistics', {
      params: filters,
    });
    return response.data;
  }

  async getCallTimeline(callLogId: number): Promise<ApiResponse<{ summary: any; events: RecordingLifecycleEvent[] }>> {
    const response = await this.api.get<ApiResponse<{ summary: any; events: RecordingLifecycleEvent[] }>>(`/admin/recording-lifecycle-events/call/${callLogId}/timeline`);
    return response.data;
  }

  // Attendance endpoints
  async getAttendances(filters?: AttendanceFilters): Promise<ApiResponse<AttendanceResponse>> {
    const response = await this.api.get<ApiResponse<AttendanceResponse>>('/admin/attendances', {
      params: filters,
    });
    return response.data;
  }

  async getAttendance(id: number): Promise<ApiResponse<{ attendance: Attendance }>> {
    const response = await this.api.get<ApiResponse<{ attendance: Attendance }>>(`/admin/attendances/${id}`);
    return response.data;
  }

  async getAttendanceStatistics(filters?: { user_id?: number; branch_id?: number; date_from?: string; date_to?: string }): Promise<ApiResponse<AttendanceStatistics>> {
    const response = await this.api.get<ApiResponse<AttendanceStatistics>>('/admin/attendances/statistics', {
      params: filters,
    });
    return response.data;
  }

  async getProductivityReport(attendanceId: number): Promise<ApiResponse<ProductivityReport>> {
    const response = await this.api.get<ApiResponse<ProductivityReport>>(`/admin/attendances/${attendanceId}/productivity-report`);
    return response.data;
  }

  async exportAttendancesExcel(filters?: AttendanceFilters): Promise<void> {
    const response = await this.api.get('/admin/attendances/export/excel', {
      params: filters,
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    try {
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance-${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      window.URL.revokeObjectURL(url);
    }
  }
}

export const apiService = new ApiService();
